"use client";

import React, { forwardRef, useCallback, useRef, useState } from "react";
import { UnitsSelector } from "../../components/UnitsSelector/UnitsSelector";
import { WhatsappHeaderButton } from "../../components/WhatsappHeaderButton/WhatsappHeaderButton";
import { PhoneHeaderButton } from "../../components/PhoneHeaderButton/PhoneHeaderButton";
import type { PhoneUnit } from "../../components/PhoneDropdown/PhoneDropdown";
import { activeHidden } from "../common/BlockVisibility";
import { useScrollState } from "../../hooks/useScrollState";
import { useHeaderMenuController } from "../../hooks/useHeaderMenuController";
import { useMeasuredHeight } from "../../hooks/useMeasuredHeight";
import { useMenuFitAtReference } from "../../hooks/useMenuFitAtReference";
import { useScrollerHeight } from "../../hooks/useScrollerHeight";
import { useViewportKind } from "../../hooks/useViewportKind";
import { BAR_LAYOUT_REM, DEFAULT_CONTACT, DEFAULT_CONTACT_RADIUS, DEFAULT_MEGA_MENU, DEFAULT_UNITS_SELECTOR } from "./constants";
import {
  clampMegaMenuColumns,
  colorsToCssVars,
  linkKey,
  megaMenuCssVars,
  resolveBlurPx,
  resolveButtonsMinRem,
  resolveMenuItemMode,
  resolveRightGroupMinRem,
  resolveSectionPosition,
  resolveSlots,
  resolveStateColors,
  splitMobileButtons,
} from "./logic";
import { STRINGS } from "./strings";
import { Logos } from "./components/Logos";
import { DesktopButton, MobileCtaButton } from "./components/ActionButtons";
import { DesktopMenu } from "./components/DesktopMenu";
import { MegaMenuPanel } from "./components/MegaMenuPanel";
import { MobileDrawer } from "./components/MobileDrawer";
import { Burger } from "./components/Icons";
import { sanitizeSvg } from "../../utils/sanitize";
import {
  ActionIconSlot,
  Actions,
  Bar,
  BarInner,
  DesktopOnly,
  GroupDivider,
  Hamburger,
  MobileOnly,
  RightGroup,
  Root,
  Surface,
  UnitBarMobile,
  UnitsSlot,
} from "./HeaderV2.styles";
import {
  BarVariant,
  BarWidth,
  BlurStrength,
  BurgerPosition,
  BurgerStyle,
  ButtonMobileDisplay,
  ContactKind,
  HeaderScrollState,
  HeaderSlot,
  HeaderV2Layout,
  IconAlignment,
  MegaMenuCardAlign,
  MegaMenuTrigger,
  MenuAlign,
  MenuItemMode,
  MenuSpacing,
  MobileDividers,
  MobileDrawerSide,
  MobileMenuMode,
  OverlapMode,
  SubmenuStyle,
  UnitsMobilePlacement,
  type HeaderV2Props,
  type MegaMenuConfig,
} from "./types";

export type { HeaderV2Props } from "./types";

const HeaderV2Inner = <T extends object>(
  {
    htmlId,
    padding,
    image,
    image2,
    showImage2 = false,
    imageScrolled,
    image2Scrolled,
    showImageScrolled = false,
    layout = HeaderV2Layout.BASIC,
    barWidth = BarWidth.WIDE,
    links,
    buttons,
    colors,
    blurEnabled = false,
    blurStrength = BlurStrength.MD,
    fixed = false,
    overlapFirstBlock = false,
    hideOnScrollDown = false,
    scrollThreshold = 48,
    megaMenu: megaMenuProp,
    megaMenuData,
    dynamicStructure,
    mobileMenuMode = MobileMenuMode.FULLSCREEN,
    mobileDrawerSide = MobileDrawerSide.RIGHT,
    burgerPosition = BurgerPosition.END,
    burgerStyle = BurgerStyle.LINES,
    mobileDividers = MobileDividers.LINES,
    menuAlign = MenuAlign.START,
    menuSpacing = MenuSpacing.COMPACT,
    submenuStyle = SubmenuStyle.ATTACHED,
    submenuRadius,
    contactRadius = DEFAULT_CONTACT_RADIUS,
    drawerBackground,
    drawerText,
    mobileBackdrop = true,
    isBuilder = false,
    forceScrollState,
    onMenuFitChange,
    units = [],
    showUnitsSelector = false,
    unitsSelector: unitsSelectorProp,
    showAllUnitsOption = false,
    allUnitsLabel = "Todas as unidades",
    unitGroupingMode,
    unitGroups,
    unitGroupingLabel,
    useGlobalQueryParams,
    showPhoneButton = false,
    phoneButton: phoneButtonProp,
    showHeaderWhatsappButton = false,
    whatsappButton: whatsappButtonProp,
    hasWhatsappItems = false,
    whatsappItems = [],
    whatsappButtonColor = "#25D366",
    whatsappSubmitUrl,
    pageEntry,
    antiBot,
    accountId,
    siteId,
    product,
    activePalette,
    hideOnMobile,
    hideOnDesktop,
    className,
  }: HeaderV2Props<T>,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const megaMenu: MegaMenuConfig = { ...DEFAULT_MEGA_MENU, ...(megaMenuProp ?? {}) };
  const megaColumns = clampMegaMenuColumns({ value: megaMenu.columns });
  const opensOnHover = (megaMenu.trigger ?? MegaMenuTrigger.HOVER) === MegaMenuTrigger.HOVER;

  const activeLinks = links?.filter((link) => link.isActive !== false) ?? [];
  const activeButtons = buttons?.filter((button) => button.isActive && button.text) ?? [];
  const slots = resolveSlots({ layout });
  const placement = resolveSectionPosition({ fixed, overlapFirstBlock, isBuilder });
  const surfaceHeight = useMeasuredHeight({ ref: surfaceRef, enabled: placement.overlap === OverlapMode.MARGIN });
  const viewportKind = useViewportKind({ ref: rootRef });
  const scrollerHeight = useScrollerHeight({ ref: rootRef });

  const { isScrolled, isHidden } = useScrollState({
    ref: rootRef,
    threshold: scrollThreshold,
    hideOnScrollDown: fixed && hideOnScrollDown,
    enabled: fixed,
  });
  const scrollState = forceScrollState ?? (isScrolled ? HeaderScrollState.SCROLLED : HeaderScrollState.TOP);
  const cssVars = {
    ...colorsToCssVars({ colors: resolveStateColors({ colors, state: scrollState }) }),
    ...megaMenuCssVars({ megaMenu }),
    ...(scrollerHeight > 0 ? { "--hv2-viewport": `${scrollerHeight}px` } : {}),
    ...(submenuRadius ? { "--hv2-submenu-radius": submenuRadius } : {}),
    "--hv2-drawer-bg": drawerBackground || "var(--hv2-submenu)",
    "--hv2-drawer-text": drawerText || "var(--hv2-submenu-text)",
  } as React.CSSProperties;
  const rootStyle: React.CSSProperties =
    placement.overlap === OverlapMode.MARGIN && surfaceHeight > 0 ? { ...cssVars, marginBottom: -surfaceHeight } : cssVars;

  const shouldRenderUnitsSelector = showUnitsSelector && units.length > 0;
  const unitsConfig = { ...DEFAULT_UNITS_SELECTOR, ...(unitsSelectorProp ?? {}) };
  const shouldRenderWhatsappButton = showHeaderWhatsappButton && (hasWhatsappItems || whatsappItems.length > 0);
  const unitsWithPhone = units.filter((u) => u.phoneNumber || (u.phones && u.phones.length > 0)) as PhoneUnit[];
  const shouldRenderPhoneButton = showPhoneButton && unitsWithPhone.length > 0;
  const mobileButtons = splitMobileButtons({ buttons: activeButtons });
  const phoneConfig = { ...DEFAULT_CONTACT, ...(phoneButtonProp ?? {}) };
  const whatsappConfig = { ...DEFAULT_CONTACT, ...(whatsappButtonProp ?? {}) };
  const hasMenuContent =
    activeLinks.length > 0 ||
    mobileButtons.drawer.length > 0 ||
    (shouldRenderPhoneButton && phoneConfig.mobileDisplay === ButtonMobileDisplay.DRAWER) ||
    (shouldRenderWhatsappButton && whatsappConfig.mobileDisplay === ButtonMobileDisplay.DRAWER);

  const menu = useHeaderMenuController({ rootRef, opensOnHover, closesOnBlur: !isBuilder });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const panelIdFor = useCallback((linkId: string) => `${htmlId || "header-v2"}-megamenu-${linkId}`, [htmlId]);

  const openMegaLink = activeLinks.find((link, index) => {
    if (linkKey({ link, index }) !== menu.openId) return false;
    return resolveMenuItemMode({ link, megaMenuData }) === MenuItemMode.MEGAMENU;
  });
  const openMegaLinkId = openMegaLink ? linkKey({ link: openMegaLink, index: activeLinks.indexOf(openMegaLink) }) : null;

  const logos = (
    <Logos
      image={image}
      image2={image2}
      showImage2={showImage2}
      imageScrolled={imageScrolled}
      image2Scrolled={image2Scrolled}
      showImageScrolled={showImageScrolled}
      scrollState={scrollState}
    />
  );

  const unitsIcon = !unitsConfig.showIcon ? null : unitsConfig.icon ? (
    <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeSvg(unitsConfig.icon) }} />
  ) : undefined;

  const unitsSelector = shouldRenderUnitsSelector ? (
    <UnitsSlot
      $style={unitsConfig.style}
      $iconRight={unitsConfig.iconPosition === IconAlignment.RIGHT}
      $mobileAlign={unitsConfig.mobileAlign}
      data-testid="header-v2-units"
      data-af-field="showUnitsSelector"
    >
      <UnitsSelector
        units={units}
        linksColor="var(--hv2-units-text)"
        icon={unitsIcon}
        showAllUnitsOption={showAllUnitsOption}
        allUnitsLabel={allUnitsLabel}
        unitGroupingMode={unitGroupingMode}
        unitGroups={unitGroups}
        unitGroupingLabel={unitGroupingLabel}
        useGlobalQueryParams={useGlobalQueryParams}
      />
    </UnitsSlot>
  ) : null;

  const whatsapp = shouldRenderWhatsappButton ? (
    <ActionIconSlot
      $kind={ContactKind.WHATSAPP}
      $radius={contactRadius}
      data-testid="header-v2-whatsapp"
      data-af-field="showHeaderWhatsappButton"
    >
      <WhatsappHeaderButton
        icon={whatsappConfig.icon}
        items={whatsappItems}
        submitUrl={whatsappSubmitUrl}
        antiBot={antiBot ?? null}
        accountId={accountId}
        siteId={siteId}
        product={product}
        buttonColor={whatsappButtonColor}
        units={units}
        unitGroupingMode={unitGroupingMode}
        unitGroups={unitGroups}
        useGlobalQueryParams={useGlobalQueryParams}
        pageEntry={pageEntry}
      />
    </ActionIconSlot>
  ) : null;

  const phone = shouldRenderPhoneButton ? (
    <ActionIconSlot $kind={ContactKind.PHONE} $radius={contactRadius} data-testid="header-v2-phone" data-af-field="showPhoneButton">
      <PhoneHeaderButton units={unitsWithPhone} icon={phoneConfig.icon} />
    </ActionIconSlot>
  ) : null;

  const hamburger = hasMenuContent ? (
    <Hamburger type="button" aria-label={STRINGS.openMenu} aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
      <Burger variant={burgerStyle} />
    </Hamburger>
  ) : null;

  const renderSlot = (slot: HeaderSlot) => {
    switch (slot) {
      case HeaderSlot.LOGO:
        return (
          <React.Fragment key={HeaderSlot.LOGO}>
            {burgerPosition === BurgerPosition.START && <MobileOnly>{hamburger}</MobileOnly>}
            {logos}
          </React.Fragment>
        );

      case HeaderSlot.MENU:
        return (
          <DesktopMenu
            key={HeaderSlot.MENU}
            links={activeLinks}
            align={menuAlign}
            spacing={menuSpacing}
            submenuStyle={submenuStyle}
            withLogo={slots.top.includes(HeaderSlot.LOGO) && slots.top.includes(HeaderSlot.MENU)}
            htmlId={htmlId}
            openId={menu.openId}
            megaMenuData={megaMenuData}
            panelIdFor={panelIdFor}
            opensOnHover={opensOnHover}
            onHoverOpen={menu.hoverOpen}
            onToggle={menu.toggle}
          />
        );

      case HeaderSlot.BUTTONS:
        return activeButtons.length > 0 ? (
          <DesktopOnly key={HeaderSlot.BUTTONS} data-header-buttons data-testid="header-v2-buttons">
            {activeButtons.map((button, index) => (
              <DesktopButton key={button.id ?? index} button={button} index={index} htmlId={htmlId} />
            ))}
          </DesktopOnly>
        ) : null;

      case HeaderSlot.ACTIONS:
        return (
          <Actions key={HeaderSlot.ACTIONS}>
            <DesktopOnly>
              {phone}
              {whatsapp}
            </DesktopOnly>
            <MobileOnly>
              {phoneConfig.mobileDisplay === ButtonMobileDisplay.BAR && phone}
              {whatsappConfig.mobileDisplay === ButtonMobileDisplay.BAR && whatsapp}
              {mobileButtons.bar.map((button, index) => (
                <MobileCtaButton key={button.id ?? index} button={button} index={index} htmlId={htmlId} />
              ))}
              {burgerPosition === BurgerPosition.END && hamburger}
            </MobileOnly>
          </Actions>
        );

      case HeaderSlot.UNITS:
        return unitsSelector ? (
          <DesktopOnly key={HeaderSlot.UNITS} data-header-units="">
            {unitsSelector}
          </DesktopOnly>
        ) : null;

      default:
        return null;
    }
  };

  const contactsCount = (phone ? 1 : 0) + (whatsapp ? 1 : 0);
  const visibleOnDesktop = (slot: HeaderSlot) =>
    slot === HeaderSlot.BUTTONS || (slot === HeaderSlot.ACTIONS && contactsCount > 0);

  const rightSlotsFor = (barSlots: HeaderSlot[]) =>
    barSlots.filter(
      (slot) =>
        slot === HeaderSlot.ACTIONS ||
        (slot === HeaderSlot.BUTTONS && activeButtons.length > 0) ||
        (slot === HeaderSlot.UNITS && !!unitsSelector),
    );

  const hasDividerBeforeUnits = (rightSlots: HeaderSlot[]) =>
    unitsConfig.divider &&
    rightSlots.includes(HeaderSlot.UNITS) &&
    rightSlots.slice(0, rightSlots.indexOf(HeaderSlot.UNITS)).some(visibleOnDesktop);

  const rightGroupMinRem = (rightSlots: HeaderSlot[]) => {
    const divider = hasDividerBeforeUnits(rightSlots);
    return resolveRightGroupMinRem({
      contacts: rightSlots.includes(HeaderSlot.ACTIONS) ? contactsCount : 0,
      buttons: rightSlots.includes(HeaderSlot.BUTTONS) ? activeButtons : [],
      units: rightSlots.includes(HeaderSlot.UNITS),
      divider,
      groups: rightSlots.length + (divider ? 1 : 0),
    });
  };

  const renderBarSlots = (barSlots: HeaderSlot[]) => {
    const left = barSlots.filter((slot) => slot === HeaderSlot.LOGO || slot === HeaderSlot.MENU).map(renderSlot);
    const rightSlots = rightSlotsFor(barSlots);
    const showDivider = hasDividerBeforeUnits(rightSlots);
    const rightStyle = {
      "--hv2-right-min": `${rightGroupMinRem(rightSlots)}rem`,
      "--hv2-buttons-min": `${resolveButtonsMinRem({ buttons: activeButtons })}rem`,
    } as React.CSSProperties;
    return (
      <>
        {left}
        {rightSlots.length > 0 && (
          <RightGroup data-testid="header-v2-right-group" data-header-right="" style={rightStyle}>
            {rightSlots.map((slot) => (
              <React.Fragment key={slot}>
                {slot === HeaderSlot.UNITS && showDivider && <GroupDivider aria-hidden="true" data-testid="header-v2-group-divider" />}
                {renderSlot(slot)}
              </React.Fragment>
            ))}
          </RightGroup>
        )}
      </>
    );
  };

  const menuBarSlots = slots.top.includes(HeaderSlot.MENU) ? slots.top : slots.bottom;
  const menuRightSlots = rightSlotsFor(menuBarSlots);
  const menuWithLogo = slots.top.includes(HeaderSlot.LOGO) && slots.top.includes(HeaderSlot.MENU);
  const menuFitSignature = [
    layout,
    barWidth,
    menuAlign,
    menuSpacing,
    ...activeLinks.map(
      (link, index) => `${linkKey({ link, index })}:${link.name}:${link.icon ? 1 : 0}:${resolveMenuItemMode({ link, megaMenuData })}`,
    ),
    ...activeButtons.map((button) => button.text),
  ].join("|");

  useMenuFitAtReference({
    rootRef,
    enabled: isBuilder && !!onMenuFitChange,
    signature: menuFitSignature,
    compact: barWidth === BarWidth.COMPACT,
    rightMinRem: menuRightSlots.length > 0 ? rightGroupMinRem(menuRightSlots) : null,
    menuMarginRem: menuWithLogo && menuAlign !== MenuAlign.CENTER ? BAR_LAYOUT_REM.menuLogoMargin : 0,
    onChange: onMenuFitChange,
  });

  return (
    <Root
      ref={setRefs}
      id={htmlId}
      $position={placement.position}
      $overlap={placement.overlap}
      data-testid="header-v2"
      data-scroll-state={scrollState}
      data-viewport={viewportKind}
      data-position={placement.position}
      data-overlap={placement.overlap}
      data-bar-width={barWidth}
      style={rootStyle}
      className={`${activePalette ?? ""} ${activeHidden({ hideOnMobile, hideOnDesktop })} ${className ?? ""}`.trim()}
    >
      <Surface
        ref={surfaceRef}
        $hidden={isHidden}
        $blur={resolveBlurPx({ enabled: blurEnabled, strength: blurStrength })}
        $padding={padding}
        data-testid="header-v2-surface"
        data-fixed-top={fixed ? "" : undefined}
        onPointerEnter={menu.onSurfaceEnter}
        onPointerLeave={menu.onSurfaceLeave}
        onBlur={menu.onSurfaceBlur}
      >
        {shouldRenderUnitsSelector && unitsConfig.mobilePlacement === UnitsMobilePlacement.BAR && (
          <UnitBarMobile $align={unitsConfig.mobileAlign} $divider={unitsConfig.mobileDivider} data-testid="header-v2-units-bar">
            {unitsSelector}
          </UnitBarMobile>
        )}
        <Bar $variant={BarVariant.TOP} $topBar={slots.bottom.length > 0} data-testid="header-v2-bar-top">
          <BarInner $compact={barWidth === BarWidth.COMPACT}>{renderBarSlots(slots.top)}</BarInner>
        </Bar>
        {slots.bottom.length > 0 && (
          <Bar $variant={BarVariant.BOTTOM} data-testid="header-v2-bar-bottom">
            <BarInner $compact={barWidth === BarWidth.COMPACT}>{renderBarSlots(slots.bottom)}</BarInner>
          </Bar>
        )}

        {openMegaLink && openMegaLinkId && (
          <MegaMenuPanel
            id={panelIdFor(openMegaLinkId)}
            link={openMegaLink}
            data={megaMenuData?.[openMegaLinkId]}
            structure={dynamicStructure}
            config={megaMenu}
            columns={megaColumns}
            htmlId={htmlId}
            onNavigate={menu.close}
          />
        )}
      </Surface>

      <MobileDrawer
        open={drawerOpen}
        mode={mobileMenuMode}
        backdrop={mobileBackdrop}
        onClose={closeDrawer}
        logo={logos}
        links={activeLinks}
        buttons={mobileButtons.drawer}
        renderUnits={
          shouldRenderUnitsSelector && unitsConfig.mobilePlacement === UnitsMobilePlacement.DRAWER
            ? (render) => (
                <UnitsSelector
                  units={units}
                  showAllUnitsOption={showAllUnitsOption}
                  allUnitsLabel={allUnitsLabel}
                  unitGroupingMode={unitGroupingMode}
                  unitGroups={unitGroups}
                  unitGroupingLabel={unitGroupingLabel}
                  useGlobalQueryParams={useGlobalQueryParams}
                >
                  {(state) => render({ state, icon: unitsIcon, showAllOption: !!showAllUnitsOption })}
                </UnitsSelector>
              )
            : undefined
        }
        contacts={
          (phone && phoneConfig.mobileDisplay === ButtonMobileDisplay.DRAWER) ||
          (whatsapp && whatsappConfig.mobileDisplay === ButtonMobileDisplay.DRAWER) ? (
            <>
              {phoneConfig.mobileDisplay === ButtonMobileDisplay.DRAWER && phone}
              {whatsappConfig.mobileDisplay === ButtonMobileDisplay.DRAWER && whatsapp}
            </>
          ) : undefined
        }
        htmlId={htmlId}
        megaMenuData={megaMenuData}
        structure={dynamicStructure}
        aspect={megaMenu.aspect}
        cardLayout={megaMenu.cardLayout}
        cardAlign={megaMenu.cardAlign ?? MegaMenuCardAlign.START}
        side={mobileDrawerSide}
        dividers={mobileDividers}
      />
    </Root>
  );
};

export const HeaderV2 = forwardRef(HeaderV2Inner) as <T extends object>(
  props: HeaderV2Props<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => JSX.Element;
